/**
 * BotSpot Web Dashboard - Bots Hook
 *
 * React hook for fetching and managing bot data with real-time updates.
 */

import { useState, useEffect, useCallback } from "react";
import { listBots } from "../api/bots";
import { getErrorMessage } from "../api/client";
import type { Bot, BotList, PaginationParams } from "../api/types";

/**
 * Hook state interface
 */
interface UseBotsState {
  bots: Bot[];
  total: number;
  page: number;
  pageSize: number;
  loading: boolean;
  error: string | null;
}

/**
 * Hook return type
 */
interface UseBotsReturn extends UseBotsState {
  refetch: () => Promise<void>;
}

/**
 * Custom hook for fetching and managing bot data
 *
 * Features:
 * - Automatic fetching on mount
 * - Periodic polling for real-time updates
 * - Manual refetch capability
 * - Error handling
 *
 * @param params - Pagination parameters
 * @param pollInterval - Polling interval in milliseconds (default: 5000, 0 to disable)
 * @returns Bot data and refetch function
 */
export function useBots(
  params?: PaginationParams,
  pollInterval: number = 5000
): UseBotsReturn {
  const [state, setState] = useState<UseBotsState>({
    bots: [],
    total: 0,
    page: params?.page || 1,
    pageSize: params?.page_size || 10,
    loading: true,
    error: null,
  });

  /**
   * Fetch bots from API
   */
  const fetchBots = useCallback(async () => {
    try {
      setState((prev) => ({ ...prev, loading: true, error: null }));

      const data: BotList = await listBots(params);

      setState({
        bots: data.items,
        total: data.total,
        page: data.page,
        pageSize: data.page_size,
        loading: false,
        error: null,
      });
    } catch (err) {
      const errorMessage = getErrorMessage(err);
      setState((prev) => ({
        ...prev,
        loading: false,
        error: errorMessage,
      }));
    }
  }, [params]);

  /**
   * Initial fetch and polling setup
   */
  useEffect(() => {
    fetchBots();

    // Set up polling if interval is specified
    if (pollInterval > 0) {
      const intervalId = setInterval(() => {
        fetchBots();
      }, pollInterval);

      return () => clearInterval(intervalId);
    }
  }, [fetchBots, pollInterval]);

  return {
    ...state,
    refetch: fetchBots,
  };
}
